import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import TextInput, { Validation } from '../../../general-ui/text-input/text';
import Modal from '../../../general-ui/modal/modal';
import RadioGroup from '../../../general-ui/radio-group/radio-group';
import Datepicker from '../../../general-ui/datepicker/datepicker';
import TagSelectInput from '../../../general-ui/tag-select/tag-select';
import LanguageSelectInput from '../../../general-ui/select/language-select';
import { CreateSession, BLAdmin, SessionTypesEnum, SessionTypesFeatures } from "../../../../types/working-model";
import TranslateLangs from '../../../../utils/translate-langs.json';
import { GetChannelTracks } from "../../../../connection/create-event";
import Timepicker from "../../../general-ui/time-picker/timepicker";
import { EventsState } from "../../../../store/types";


import "react-datepicker/dist/react-datepicker.css";
import { editSessionAgenda, updateEditingSessionByUid, updateHomepageMainNav } from "../../../../store/actions/admin/create-event";
import Icon, { COLORS, ICONS } from "../../../general-ui/icon";
import useRequiredFields from "../../../../utils/use-required-fields";
import classNames from "classnames";
import { GetDefaultMainNavItems } from "../../../../store/utils/create-event";

export const PrimaryTooltip = (): JSX.Element => <Icon name={ICONS.PRIMARY_TOOLTIP} color={COLORS.CYAN} size={12}></Icon>;

const COEFF = 1000 * 60 * 5;

interface SessionModalProps {
	addingSession: boolean;
	closeModal: () => void;
	createSession: (session: CreateSession) => Promise<any>;
	initialValues?: CreateSession | null;
	addingSessionType?: SessionTypesEnum;
}

function getEmptySession(user: BLAdmin) {
	return {
		title: '',
		channel: user.active_channel,
		languages: ['en'],
		default_language: "en",
		tracks: [],
		session_type: SessionTypesEnum.broadcast,
	};
}

const SessionModal: React.FC<SessionModalProps> = ({
	addingSession,
	closeModal,
	createSession,
	addingSessionType = SessionTypesEnum.broadcast,
}) => {
	const {
		AuthReducer: {
			user,
			token,
		},
		CreateEventReducer: {
			editingSessionAgenda,
			newEventRequest,
			workingEvent
		},
	} = useSelector((state: EventsState) => state);
	const dispatch = useDispatch();
	const workingSession = useRef<CreateSession>(getEmptySession(user!));
	const [sessionType, setSessionType] = useState<SessionTypesEnum>(addingSessionType);
	const [defaultTracks, setDefaultTracks] = useState<string[]>([]);
	const [duration, setDuration] = useState(60);
	const [durationIsSet, setDurationIsSet] = useState(true);
	const [sessionName, setSessionName] = useState("");
	const dateForFive = new Date();
	const [date, setDate] = useState<Date | null>(editingSessionAgenda?.start_timestamp ? new Date(editingSessionAgenda.start_timestamp) : new Date(Math.ceil(dateForFive.getTime() / COEFF) * COEFF));
	const [initialized, setInitialized] = useState(false);
	const [pastEventOpen, setPastEventOpen] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [submitting, setSubmitting] = useState(false);

	const {
		requiredFields,
		updateRequiredField,
		resetRequiredFields,
	} = useRequiredFields(['title', 'duplicateName'], {
		title: (string: string) => !!string.trim(),
		// if the name is duplicate then return false to trigger validation
		duplicateName: (isDuplicate: boolean) => {
			if (isDuplicate) setErrorMessage('Session name already used for this Event');
			if (!isDuplicate) setErrorMessage('');
			return !isDuplicate;
		}
	});

	const sessionNames: { name: string, id: string | undefined }[] = useMemo(() => {
		const existingNames = workingEvent ? workingEvent.sessions.map(session => ({ name: session.title.base, id: session.session.toString() })) : [];
		const requestNames = newEventRequest ? newEventRequest.sessions.map(session => ({ name: session.title, id: session?.uid })) : [];
		return [...existingNames, ...requestNames];
	}, [workingEvent, newEventRequest]);

	const checkName = useCallback((sessionName: string) => {
		// if same name as a different session, then duplicate
		const isDuplicate = sessionNames.some(name => name.name === sessionName && workingSession.current.uid !== name.id);
		updateRequiredField('duplicateName', isDuplicate);
	}, [sessionNames, updateRequiredField]);

	const handleKeyUp = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
		checkName(((e.target as HTMLInputElement).value));
	}, [checkName]);

	// if editing an already created session, initialize all values
	const intitialDuration = useCallback(() => {
		if (editingSessionAgenda?.start_timestamp && editingSessionAgenda?.end_timestamp) {
			return (editingSessionAgenda?.end_timestamp - editingSessionAgenda?.start_timestamp) / 1000 / 60;
		}
		return 60;
	}, [editingSessionAgenda?.end_timestamp, editingSessionAgenda?.start_timestamp]);


	useEffect(() => {
		if (editingSessionAgenda) {
			workingSession.current = editingSessionAgenda;
			setSessionType(editingSessionAgenda.session_type);
			setSessionName(editingSessionAgenda.title);
			setDefaultTracks(editingSessionAgenda.tracks);
			if (SessionTypesFeatures[editingSessionAgenda.session_type].has_start_time) {
				if (editingSessionAgenda.start_timestamp && editingSessionAgenda.end_timestamp) {
					setDate(new Date(editingSessionAgenda.start_timestamp));
					setDuration(intitialDuration());
					setDurationIsSet(true);
				}
			}
		}
	}, [editingSessionAgenda, intitialDuration]);

	useEffect(() => {
		// if the modal is opened by clicking + Add for a new broadcast session, it
		// auto defaults to on demand, so we need to make sure we initialize the appropriate values if it's not on demand
		if (!editingSessionAgenda && SessionTypesFeatures?.[addingSessionType]?.has_start_time && !initialized && date) {
			setInitialized(true);
			workingSession.current.session_type = addingSessionType;
			workingSession.current.start_timestamp = date.valueOf();
			workingSession.current.end_timestamp = date.valueOf() + (1000 * 60 * duration);
			setDurationIsSet(true);
		}
	}, [editingSessionAgenda, date, addingSessionType, duration, initialized]);

	const handleCancel = () => {
		resetRequiredFields();
		dispatch(editSessionAgenda(null));
		workingSession.current = getEmptySession(user!);
		setSessionName('');
		setDefaultTracks([]);
		setSessionType(SessionTypesEnum.broadcast);
		closeModal();
	};

	async function finished() {
		setSubmitting(true);

		// if the session is broadcast, make sure start time is set for in the present or future
		if (sessionType === SessionTypesEnum.broadcast && workingSession.current.start_timestamp) {
			if (workingSession.current.start_timestamp < Date.now()) {
				setPastEventOpen(true);
				return;
			}
		}

		// if editing a boradcast session, make sure new start time is in the present or future
		if (editingSessionAgenda && date) {
			if (date.getTime() < Date.now()) {
				setPastEventOpen(true);
				return;
			}
		}

		// if creating a session that has a date value, but working session timestamps aren't set, set them here
		if (sessionType === SessionTypesEnum.broadcast) {
			if (!workingSession.current.start_timestamp && date) {
				workingSession.current.start_timestamp = date.valueOf();
				workingSession.current.end_timestamp = date.valueOf() + (1000 * 60 * duration);
			}
		}

		// adding on_demand bool for backwards compatability, though it's not used anywhere anymore
		if (SessionTypesFeatures[workingSession.current.session_type].has_start_time) {
			workingSession.current.on_demand = true;
		} else {
			workingSession.current.on_demand = false;
		}

		// if no times required, delete them
		if (!SessionTypesFeatures[workingSession.current.session_type].has_start_time) {
			delete workingSession.current.start_timestamp;
			delete workingSession.current.end_timestamp;
		}

		if (editingSessionAgenda) {
			dispatch(updateEditingSessionByUid(workingSession.current));
			// reset original values
			setSessionType(SessionTypesEnum.broadcast);
			setDefaultTracks([]);
			setDuration(60);
			setDurationIsSet(true);
			setSessionName('');
			setInitialized(false);
			setPastEventOpen(false);
		} else {
			await createSession(workingSession.current);
			// if adding session to a single session event, get updated event_main_nav_items
			if (workingEvent?.sessions.length === 1) {
				workingEvent?.homepage?.homepage && dispatch(updateHomepageMainNav(GetDefaultMainNavItems()));
			}
		}
		// clear ref to prevent overrides after editing an event and creating a new one
		workingSession.current = getEmptySession(user!);
		setSubmitting(false);
	}

	function handleSetDate(date: Date) {
		if (workingSession.current) {
			if (date) {
				setDate(date);
				workingSession.current.start_timestamp = date.valueOf();
				workingSession.current.end_timestamp = date.valueOf() + (1000 * 60 * duration);
			}
		}
	}

	function handleSetDuration(e: React.ChangeEvent<HTMLInputElement>) {
		if (workingSession.current) {
			setDuration(e.target.valueAsNumber);

			setDurationIsSet(!isNaN(e.target.valueAsNumber));

			if (!isNaN(e.target.valueAsNumber)) {
				if (workingSession.current.start_timestamp) { workingSession.current.end_timestamp = workingSession.current.start_timestamp + (1000 * 60 * e.target.valueAsNumber); }
			}
		}

	}

	function handleRadio(value: any) {
		setSessionType(value);
		if (workingSession.current) {
			workingSession.current.session_type = value;
			if (SessionTypesFeatures[workingSession.current.session_type].has_start_time) {
				handleSetDate(date || new Date(Math.ceil(dateForFive.getTime() / COEFF) * COEFF)); // reset date to avoid issues with invalid form data
				setDurationIsSet(true);
			}
		}
	}

	function setTracks(tracks: string[]) {
		if (workingSession.current) {
			// filter out any track thats are empty strings
			const filteredTracks = tracks.filter(track => track.trim());
			workingSession.current.tracks = filteredTracks;
		}
	}

	function setLanguages(langs: string[], defaultLang: string) {
		if (workingSession.current) {
			if (!langs) {
				langs = ['en'];
			}
			if (langs.length === 0) {
				langs[0] = 'en';
			}
			if (!defaultLang || !langs.includes(defaultLang)) {
				// if english exists in langs, use it
				// else use first language
				if (langs.includes('en')) {
					defaultLang = 'en';
				} else {
					defaultLang = langs[0];
				}
			}
			workingSession.current.languages = langs;
			workingSession.current.default_language = defaultLang;
		}
	}

	function handleSessionName(e: React.ChangeEvent<HTMLInputElement>) {
		if (workingSession.current) {
			workingSession.current.title = e.target.value;
			setSessionName(e.target.value);
			updateRequiredField('title', e.target.value);
		}
	}

	const loadTracksMemoized = useCallback(() => async () => {
		try {
			const tracks = await GetChannelTracks(user!.active_channel, token!);

			setDefaultTracks(tracks);
		} catch (e) {
			console.error(e);
		}
	}, [token, user]);

	function formValid() {
		const validTitle = requiredFields.title === Validation.normal && requiredFields.duplicateName === Validation.normal;
		if (!SessionTypesFeatures[workingSession.current.session_type].has_start_time) {
			return !!sessionName && validTitle;
		} else {
			return !!sessionName && validTitle && !!date && durationIsSet && !isNaN(duration);
		}
	}

	useEffect(() => {
		//reload the tracks whenever the user opens this modal to get new tracks they've just added
		if (addingSession) {
			loadTracksMemoized()();
		} else {
			//reset the whole thing on close
			workingSession.current = getEmptySession(user!);
			setSessionType(addingSessionType);
			setDuration(60);
			// setDurationIsSet(false);
			// setDate(null);
		}
	}, [addingSession, loadTracksMemoized, token, user, addingSessionType]);

	const closePastEvent = useCallback(() => {
		setSubmitting(false);
		setPastEventOpen(false);
	}, []);

	const validName = (requiredFields.title === Validation.normal && requiredFields.duplicateName === Validation.normal) ? Validation.normal : Validation.error;

	return (
		<>
			<Modal
				open={addingSession || !!editingSessionAgenda}
				onRequestClose={closeModal}
				title={editingSessionAgenda ? "Edit Session" : "New Session"}
				cancellable={false}
				closeable={false}
				className="session-modal"
				footer={(
					<div className="session-modal-footer">
						<span className={classNames("error-message", { toast: errorMessage })}>{errorMessage}</span>
						<div className="action-buttons">
							<button onClick={handleCancel} style={{ marginRight: 16 }}>Cancel</button>
							<button disabled={!formValid() || submitting} onClick={finished} className="primary">{editingSessionAgenda ? 'Update' : 'Add'}</button>
						</div>
					</div>
				)}
				showOverflow
			>
				<>
					<TextInput
						label={"Session Title *"}
						tooltip={[PrimaryTooltip, 'This is the first impression you make. Try to make it useful and convey the idea that the main benefit is somehow unique.']}
						onChange={handleSessionName}
						placeholder={"Enter session title"}
						defaultValue={editingSessionAgenda?.title || ''}
						onBlur={(e) => updateRequiredField('title', e.target.value)}
						valid={validName}
						onKeyUp={handleKeyUp}
					/>

					<TagSelectInput
						label={"Tracks"}
						tooltip={[PrimaryTooltip, 'Tracks help organize and distribute content in a meaningful way.']}
						placeholder={"Select or enter tracks"}
						onChange={setTracks}
						defaultTags={defaultTracks}
						initialTags={editingSessionAgenda?.tracks || []}
						dropdownContainerStyles={{
							maxWidth: 'calc(100% - 80px)',
							margin: '0 auto',
						}}
						noWhitespace
						maxTags={20}
						onError={(s) => {
							if (s === 'MAX TAGS') setErrorMessage('Twenty is the max number of tracks');
							setTimeout(() => setErrorMessage(''), 2500);
						}}
					/>

					<LanguageSelectInput
						label={"Languages"}
						tooltip={[PrimaryTooltip, "Creating a session in multiple languages let's you reach the widest possible audience"]}
						placeholder={"Select languages"}
						onChange={setLanguages}
						languages={TranslateLangs as any}
						initialValues={editingSessionAgenda?.languages || ["en"]}
						initialDefaultLanguage={editingSessionAgenda?.default_language || 'en'}
					/>

					{/* the different session types */}
					<RadioGroup
						options={
							// FUTURE DEV - remove filter for future implementation of breakout rooms
							// Object.values(SessionTypesFeatures).map(value => ({
							Object.values(SessionTypesFeatures).filter(value => value.name !== "breakout").map(value => ({
								value: value.name,
								label: value.display_name,
							}))
						}
						onChange={handleRadio}
						selected={sessionType}
						defaultValue={editingSessionAgenda
							? editingSessionAgenda.session_type
							: addingSessionType
								? addingSessionType
								: SessionTypesEnum.onDemand
						}
					/>

					{SessionTypesFeatures[sessionType].has_start_time && (
						<div className="field-group broadcast-group">
							<div className="broadcast-date">
								<Datepicker
									onChange={handleSetDate}
									label={"Date & Time *"}
									defaultValue={editingSessionAgenda?.start_timestamp ? new Date(editingSessionAgenda.start_timestamp) : (date || undefined)}
									popperPlacement={'right'}
									portalId="date-portal"
									value={date!}
								/>
							</div>
							<div className="broadcast-time">
								<Timepicker
									onChange={handleSetDuration}
									label={"Duration (minutes) *"}
									placeholder={"Enter a duration in minutes"}
									initialValue={intitialDuration() || 0}
								/>
							</div>
						</div>
					)}
				</>
			</Modal>
			<Modal
				open={pastEventOpen}
				onRequestClose={closePastEvent}
				cancellable={false}
				closeable={false}
				title={"Events in the past"}
				footer={
					<div style={{ marginLeft: 'auto', marginRight: 'auto' }}>
						<button className={'primary'} onClick={closePastEvent}>Close</button>
					</div>
				}
			>
				<div className="past-events-modal" style={{ marginTop: 8 }}>
					<p>Events must be scheduled in the present or future.</p>
				</div>

			</Modal>
		</>
	);
};
export default SessionModal;
